import { useQuery } from '@tanstack/react-query';
import { errorText, useApi } from '../lib/api';
import {
  fmt,
  km,
  msWhen,
  newestFirst,
  platformLabel,
  playerHealth,
  rowDay,
  tierChipClass,
  type DayActivity,
} from '../lib/health/healthApi';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

function DayLine({ label, d }: { label: string; d?: DayActivity }) {
  if (!d) return null;
  return (
    <li>
      <span className="font-semibold">{label}</span> ({d.dayLocal}): {d.band ?? 'rest'} · credited{' '}
      {fmt(d.creditedMin)} min · effort {fmt(d.effortMin)} · active {fmt(d.activeMin)} ·{' '}
      {km(d.distanceM)} · {d.sessions ?? 0} sessions · best tier {d.bestTier ?? 0}
    </li>
  );
}

/**
 * A player's Activity (health intake, CONTRACT §3/§5; `GET /admin/players/{id}/health`): the health links, the standard activity
 * profile (today/yesterday bands, streak, level, week totals, the last-7 strip) and the workout ledger, newest first, with each
 * row's tier chip, flags and credit. Read-only: it's the debugging surface for scoring, and admins may see the values.
 */
export function PlayerActivitySection({ playerId }: { playerId: string }) {
  const { client } = useApi();
  const q = useQuery({
    queryKey: ['admin-player-health', playerId],
    queryFn: () => playerHealth(client, playerId),
  });
  const d = q.data;
  const p = d?.profile;
  const rows = newestFirst(d?.rows ?? []);

  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <h2 className="font-semibold">Activity</h2>
      {q.isPending && <p className="text-sm">Loading…</p>}
      {q.error && (
        <p className="text-sm text-blam">
          {errorText(q.error).includes('404')
            ? 'Not available yet (an older API).'
            : errorText(q.error)}
        </p>
      )}
      {d && (
        <>
          <h3 className="mt-2 text-sm font-semibold">Links</h3>
          {(d.links ?? []).length === 0 ? (
            <p className="text-sm">Not connected.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className={th}>Platform</th>
                  <th className={th}>Device</th>
                  <th className={th}>Granted</th>
                  <th className={th}>Connected</th>
                  <th className={th}>Last sync</th>
                  <th className={th}>Last error</th>
                </tr>
              </thead>
              <tbody>
                {(d.links ?? []).map((l) => (
                  <tr key={`${l.platform}|${l.deviceId}`} className="border-t border-ink/20">
                    <td className={td}>{platformLabel(l.platform)}</td>
                    <td className={`${td} font-mono`}>{l.deviceId}</td>
                    <td className={td}>{(l.granted ?? []).join(', ') || '—'}</td>
                    <td className={td}>{msWhen(l.connectedMs)}</td>
                    <td className={td}>{msWhen(l.lastSyncMs)}</td>
                    <td className={`${td} ${l.lastError ? 'text-blam' : ''}`}>
                      {l.lastError ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {p && (
            <>
              <h3 className="mt-3 text-sm font-semibold">Profile</h3>
              <ul className="text-xs">
                <li>
                  Level <span className="font-semibold">{p.level ?? 'dormant'}</span> (
                  {p.levelIndex ?? 0}) · streak {p.streakDays ?? 0} d (best {p.bestStreakDays ?? 0})
                  · last sync {msWhen(p.lastSyncMs)}
                </li>
                <li>
                  Week: credited {fmt(p.weekCreditedMin)} min · effort {fmt(p.weekEffortMin)} ·
                  active {fmt(p.weekActiveMin)} · {km(p.weekDistanceM)} · {p.weekSessions ?? 0}{' '}
                  sessions
                </li>
                <DayLine label="Today" d={p.today} />
                <DayLine label="Yesterday" d={p.yesterday} />
              </ul>
              {(p.last7?.length ?? 0) > 0 && (
                <div className="mt-2 flex gap-1" aria-label="Last 7 days">
                  {p.last7!.map((day) => (
                    <div
                      key={day.dayLocal}
                      title={`${day.dayLocal}: ${fmt(day.creditedMin)} credited min`}
                      className="w-16 rounded border border-ink px-1 text-center text-[10px]"
                    >
                      <div>{day.dayLocal?.slice(5)}</div>
                      <div className="font-semibold">{day.band ?? 'rest'}</div>
                      <div>{fmt(day.creditedMin)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <h3 className="mt-3 text-sm font-semibold">Workouts ({rows.length})</h3>
          {rows.length === 0 ? (
            <p className="text-sm">No workouts.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className={th}>Day</th>
                  <th className={th}>Category</th>
                  <th className={th}>Source app</th>
                  <th className={th}>Device</th>
                  <th className={th}>Recording</th>
                  <th className={th}>Active min</th>
                  <th className={th}>Distance</th>
                  <th className={th}>HR avg</th>
                  <th className={th}>Tier</th>
                  <th className={th}>Flags</th>
                  <th className={th}>Effort / credited</th>
                  <th className={th}>Duplicate of</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id ?? r.sourceId} className="border-t border-ink/20">
                    <td className={td}>{rowDay(r)}</td>
                    <td className={td}>{r.category}</td>
                    <td className={`${td} font-mono`}>{r.sourceApp}</td>
                    <td className={td}>{r.deviceType}</td>
                    <td className={td}>{r.recording}</td>
                    <td className={td}>{fmt((r.activeSec ?? 0) / 60)}</td>
                    <td className={td}>{km(r.distanceM)}</td>
                    <td className={td}>{fmt(r.hrAvg)}</td>
                    <td className={td}>
                      <span className={`rounded px-1.5 font-semibold ${tierChipClass(r.tier)}`}>
                        {r.tier ?? 0}
                      </span>
                    </td>
                    <td className={td}>{(r.flags ?? []).join(', ')}</td>
                    <td className={td}>
                      {fmt(r.effortMin, 1)} / {fmt(r.creditedMin, 1)}
                    </td>
                    <td className={`${td} font-mono`}>{r.duplicateOf ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
